import { SignupAnalyticsCount } from 'app/customer-dashboard/customer-dashboard-data.service';
import { WidgetDailyStatisticsResponse } from 'app/campaign/widgets.model';
import {
  DataPlatformQueryRequest,
  DataPlatformQueryResponse,
  DP_FACETS,
  DP_METRICS,
  DpMetric,
} from 'app/data-platform/data-platform.model';
import { buildEventFilter, buildSeries, normaliseDayKey } from './widget-analytics.adapter';

/**
 * Migration adapter — DataPlatform signup-analytics responses → legacy TS shapes.
 *
 * The legacy `signup-analytics/count` and `count/by-sites` endpoints accept a
 * widget-shaped request (`{ startDate, endDate, widgetIds, ... }`) and return
 * either `{ createdSubscribers, createdMembers, duplicatesPrevented }` or an
 * array of those with `siteId` attached. The new DataPlatform endpoints accept
 * `{ siteIds, from, to }` and return either the same single object or
 * `{ items: [{ siteId, ...counts }] }`.
 */

const SIGN_UP_GRAPH_SERIES: ReadonlyArray<[DpMetric, string]> = [
  [DP_METRICS.signupsSubscribers, 'CreatedSubscribers'],
  [DP_METRICS.signupsMembers, 'CreatedMembers'],
  [DP_METRICS.signupsDuplicatesPrevented, 'DuplicatesPrevented'],
];

const PERIOD_COLUMN = 'period';

export interface DpSignUpCountRequest {
  siteIds: number[];
  from?: string;
  to?: string;
}

export interface DpSignUpCountBySiteRequest {
  siteIds: number[];
  from?: string;
  to?: string;
}

export interface DpSignUpCountBySiteItem {
  siteId: number;
  createdSubscribers: number;
  createdMembers: number;
  duplicatesPrevented: number;
}

export interface DpSignUpCountBySiteResponse {
  items: DpSignUpCountBySiteItem[];
}

export function buildSignUpCountRequest(
  siteIds: number[],
  startDate: string | Date | undefined,
  endDate: string | Date | undefined,
): DpSignUpCountRequest {
  const request: DpSignUpCountRequest = { siteIds };
  if (startDate !== undefined && endDate !== undefined) {
    request.from = toIsoString(startDate);
    request.to = toIsoString(endDate);
  }
  return request;
}

export function mapSignUpCountBySiteResponse(response: DpSignUpCountBySiteResponse): SignupAnalyticsCount[] {
  return response.items.map((item) => ({
    siteId: item.siteId,
    createdSubscribers: item.createdSubscribers,
    createdMembers: item.createdMembers,
    duplicatesPrevented: item.duplicatesPrevented,
  }));
}

export function buildSignUpGraphQuery(
  siteIds: number[],
  startDate: string | Date,
  endDate: string | Date,
  widgetIds: number[],
): DataPlatformQueryRequest {
  return {
    filter: buildEventFilter(siteIds, startDate, endDate, widgetIds),
    metrics: SIGN_UP_GRAPH_SERIES.map(([metric]) => metric),
    facets: [DP_FACETS.time],
    granularity: 'day',
  };
}

export function mapSignUpGraphResponse(
  response: DataPlatformQueryResponse,
  startDate: string | Date,
  endDate: string | Date,
): WidgetDailyStatisticsResponse {
  const items = SIGN_UP_GRAPH_SERIES.map(([metric, seriesName]) => {
    const dayTotals = new Map<string, number>();
    for (const row of response.rows) {
      const value = row[metric];
      if (typeof value === 'number') {
        const dayKey = normaliseDayKey(String(row[PERIOD_COLUMN] ?? ''));
        dayTotals.set(dayKey, (dayTotals.get(dayKey) ?? 0) + value);
      }
    }
    return buildSeries(seriesName, dayTotals, startDate, endDate, true);
  });
  return {
    data: {
      name: 'signup-analytics',
      count: items.reduce((sum, series) => sum + series.count, 0),
      items,
    },
  };
}

function toIsoString(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : value;
}
